import CloseIcon from "@mui/icons-material/Close";
import { useCallback, useEffect, useMemo, useState } from "react";
import baseUrl from "../../api/baseUrl";
import { ModalOverlay } from "../ModalOverlay";
import LabPriceInputs from "./LabPriceInputs";
import {
  type PriceListRow,
  type Prices,
  MISSING_ITEM_HINT,
  emptyPrices,
  findCatalogItem,
  priceSummary,
} from "./labCatalog";
import type { Partner } from "./labPartnerTypes";
import { useLabCatalog } from "./useLabCatalog";

const fieldClass =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900";
const labelClass = "mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400";

function errorText(err: unknown, fallback: string) {
  const e = err as { response?: { data?: { message?: string } } };
  return e?.response?.data?.message || fallback;
}

function priceBody(method: PriceListRow["pricingMethod"], p: Prices) {
  return method === "base_plus_tooth"
    ? { basePrice: p.basePrice, toothPrice: p.toothPrice, includedTeeth: p.includedTeeth ?? 0 }
    : { price: p.price };
}

type LocalLabPriceListModalProps = {
  partner: Partner | null;
  onClose: () => void;
};

/**
 * Price list for a lab the clinic added by hand. Every item comes from the
 * Care32 catalogue; the clinic sets its price (optional — it can be filled in
 * from an order later). Orders keep their own copy, so edits here never
 * change past orders. An item orders use is set inactive instead of removed.
 */
export default function LocalLabPriceListModal({ partner, onClose }: LocalLabPriceListModalProps) {
  const { catalog } = useLabCatalog(!!partner);
  const [items, setItems] = useState<PriceListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [addId, setAddId] = useState("");
  const [addPrices, setAddPrices] = useState<Prices>(emptyPrices());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editPrices, setEditPrices] = useState<Prices>(emptyPrices());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const partnerId = partner?.id;

  const load = useCallback(() => {
    if (!partnerId) return;
    setLoading(true);
    baseUrl
      .get(`client/labs/partners/${partnerId}/items`)
      .then((res) => setItems(res.data.data ?? []))
      .catch((err) => setError(errorText(err, "Could not load price list")))
      .finally(() => setLoading(false));
  }, [partnerId]);

  useEffect(() => {
    if (!partnerId) return;
    setItems([]);
    setAddId("");
    setEditingId(null);
    setError("");
    load();
  }, [partnerId, load]);

  useEffect(() => {
    if (!partner) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && !busy) onClose();
    }
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [partner, busy, onClose]);

  const onList = useMemo(() => new Map(items.map((i) => [i.catalogItemId, i])), [items]);
  const picked = addId ? findCatalogItem(catalog, addId) : null;
  const already = addId ? onList.get(addId) : undefined;

  if (!partner) return null;

  async function run(action: () => Promise<unknown>, fallback: string) {
    setBusy(true);
    setError("");
    try {
      await action();
      return true;
    } catch (err) {
      setError(errorText(err, fallback));
      return false;
    } finally {
      setBusy(false);
    }
  }

  function replace(row: PriceListRow) {
    setItems((prev) => prev.map((i) => (i.id === row.id ? row : i)));
  }

  async function addItem() {
    if (!picked) {
      setError("Pick an item from the catalogue");
      return;
    }
    if (already) {
      setEditingId(already.id);
      setEditPrices({ ...emptyPrices(), ...already.prices });
      setAddId("");
      return;
    }
    const ok = await run(async () => {
      const res = await baseUrl.post(`client/labs/partners/${partner!.id}/items`, {
        catalogItemId: picked.item.id,
        ...priceBody(picked.item.pricingMethod, addPrices),
      });
      setItems((prev) => [...prev, res.data.data]);
    }, "Could not add item");
    if (ok) {
      setAddId("");
      setAddPrices(emptyPrices());
    }
  }

  async function saveEdit(row: PriceListRow) {
    const ok = await run(async () => {
      const res = await baseUrl.patch(
        `client/labs/partners/${partner!.id}/items/${row.id}`,
        priceBody(row.pricingMethod, editPrices)
      );
      replace(res.data.data);
    }, "Could not save item");
    if (ok) setEditingId(null);
  }

  async function setStatus(row: PriceListRow, status: "active" | "inactive") {
    await run(async () => {
      const res = await baseUrl.patch(`client/labs/partners/${partner!.id}/items/${row.id}`, { status });
      replace(res.data.data);
    }, "Could not update item");
  }

  async function remove(row: PriceListRow) {
    if (!window.confirm(`Remove "${row.name}" from the price list?`)) return;
    setBusy(true);
    setError("");
    try {
      await baseUrl.delete(`client/labs/partners/${partner!.id}/items/${row.id}`);
      setItems((prev) => prev.filter((i) => i.id !== row.id));
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409 && window.confirm(`Orders use "${row.name}". Set it inactive instead? It leaves the order form but stays on those orders.`)) {
        setBusy(false);
        await setStatus(row, "inactive");
        return;
      }
      setError(errorText(err, "Could not remove item"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalOverlay
      className="fixed inset-0 z-[200] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="local-lab-price-list-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        className="relative flex max-h-[min(92dvh,860px)] w-full max-w-3xl flex-col rounded-t-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800 sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3.5 dark:border-slate-700 sm:px-6">
          <div>
            <h2 id="local-lab-price-list-title" className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Price list · {partner.name}
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Items come from the Care32 catalogue. A price can be left empty and added from an order later. Changing a price
              here doesn&apos;t change past orders.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="flex size-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 22 }} />
          </button>
        </header>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-6">
          {error ? (
            <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
              {error}
            </p>
          ) : null}

          <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4 dark:border-slate-600 dark:bg-slate-800/50">
            <div>
              <label htmlFor="price-list-item" className={labelClass}>
                Item
              </label>
              <select
                id="price-list-item"
                value={addId}
                onChange={(e) => {
                  setAddId(e.target.value);
                  setAddPrices(emptyPrices());
                }}
                className={fieldClass}
              >
                <option value="">Choose from the catalogue…</option>
                {catalog.map((c) => (
                  <optgroup key={c.id} label={c.name}>
                    {c.items.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.name}
                        {onList.has(i.id) ? " — on your list" : ""}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">{MISSING_ITEM_HINT}</p>
            </div>
            {picked && !already ? (
              <LabPriceInputs method={picked.item.pricingMethod} value={addPrices} onChange={setAddPrices} />
            ) : null}
            {already ? (
              <p className="text-sm text-amber-800 dark:text-amber-200">
                {already.name} is already on this list. Add opens it for editing.
              </p>
            ) : null}
            <button
              type="button"
              onClick={addItem}
              disabled={busy || !picked}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {already ? "Edit it" : "Add to price list"}
            </button>
          </div>

          {loading ? (
            <p className="py-6 text-center text-sm text-slate-500">Loading…</p>
          ) : items.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500">No items on this list yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/80 dark:border-slate-700">
                    <th className="px-3 py-2.5 font-medium text-slate-500">Item</th>
                    <th className="px-3 py-2.5 font-medium text-slate-500">Category</th>
                    <th className="px-3 py-2.5 font-medium text-slate-500">Price</th>
                    <th className="px-3 py-2.5 text-right font-medium text-slate-500">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => {
                    const inactive = row.status === "inactive";
                    return (
                      <tr key={row.id} className="border-b border-slate-100 align-top last:border-b-0 dark:border-slate-800">
                        <td className="px-3 py-2.5">
                          <span className={inactive ? "text-slate-400 line-through" : "font-medium text-slate-900 dark:text-slate-100"}>
                            {row.name}
                          </span>
                          {inactive ? <span className="ml-2 text-xs text-slate-500">inactive</span> : null}
                        </td>
                        <td className="px-3 py-2.5 text-slate-600">{row.categoryName}</td>
                        <td className="px-3 py-2.5">
                          {editingId === row.id ? (
                            <LabPriceInputs method={row.pricingMethod} value={editPrices} onChange={setEditPrices} />
                          ) : (
                            <span className="tabular-nums text-slate-700 dark:text-slate-200">
                              {priceSummary(row.pricingMethod, row.prices)}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          {editingId === row.id ? (
                            <div className="flex justify-end gap-2">
                              <button type="button" onClick={() => setEditingId(null)} className="text-xs text-slate-500">
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={() => saveEdit(row)}
                                disabled={busy}
                                className="text-xs font-semibold text-primary"
                              >
                                Save
                              </button>
                            </div>
                          ) : (
                            <div className="flex justify-end gap-3">
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingId(row.id);
                                  setEditPrices({ ...emptyPrices(), ...row.prices });
                                }}
                                className="text-xs font-medium text-primary"
                              >
                                Edit price
                              </button>
                              {inactive ? (
                                <button type="button" onClick={() => setStatus(row, "active")} className="text-xs font-medium text-slate-600">
                                  Reactivate
                                </button>
                              ) : (
                                <button type="button" onClick={() => remove(row)} className="text-xs font-medium text-red-600">
                                  Remove
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}
